import React, { useState } from 'react';
import FacetMultiSelect from './FacetMultiSelect';
import { SCORE_PRESETS, ALIGNMENT_PRESETS, PART_STRENGTH_PRESETS } from './api';

// Selection-driven filters. Values are prepopulated from /facets (with counts)
// so operators pick rather than type. Free-text regex lives under "Advanced".
export default function FilterBar({ filters, onChange, onApply, onReset, loading, facets }) {
  const [showAdvanced, setShowAdvanced] = useState(false);
  const f = filters || {};
  const fx = facets || {};
  const set = (patch) => onChange({ ...f, ...patch });

  const countMap = (buckets) =>
    (buckets || []).reduce((acc, b) => ({ ...acc, [b.key]: b.count }), {});
  const scoreCounts = countMap(fx.score_buckets);
  const alignCounts = countMap(fx.alignment_buckets);

  // A single-select segmented control; clicking the active option clears it.
  const Segmented = ({ label, presets, value, onPick, counts }) => (
    <div>
      <label className="form-label small mb-1 d-block">{label}</label>
      <div className="btn-group btn-group-sm flex-wrap" role="group">
        {presets.map((p) => {
          const active = value === p.key;
          const c = counts ? counts[p.key] : undefined;
          return (
            <button
              key={p.key}
              type="button"
              className={`btn ${active ? 'btn-primary' : 'btn-outline-secondary'}`}
              onClick={() => onPick(active ? '' : p.key)}
              disabled={counts && c === 0 && !active}
            >
              {p.label}
              {c !== undefined && <span className="badge text-bg-light ms-1">{c}</span>}
            </button>
          );
        })}
      </div>
    </div>
  );

  return (
    <div className="card mb-3">
      <div className="card-body">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <h6 className="mb-0">Filters</h6>
          <span className="text-muted small">Pick values, then bulk-approve or reject what matches.</span>
        </div>

        {/* Quick one-click presets */}
        <div className="d-flex flex-wrap gap-2 mb-3">
          <button
            type="button"
            className={`btn btn-sm ${f.alignmentBucket === 'mismatch' ? 'btn-warning' : 'btn-outline-warning'}`}
            onClick={() => set({ alignmentBucket: f.alignmentBucket === 'mismatch' ? '' : 'mismatch' })}
          >
            Manufacturer mismatches
            {alignCounts.mismatch !== undefined && (
              <span className="badge text-bg-light ms-1">{alignCounts.mismatch}</span>
            )}
          </button>
          <button
            type="button"
            className={`btn btn-sm ${f.scoreBucket === 'b30_50' ? 'btn-info' : 'btn-outline-info'}`}
            onClick={() => set({ scoreBucket: f.scoreBucket === 'b30_50' ? '' : 'b30_50' })}
          >
            Partial band (30–50)
            {scoreCounts.b30_50 !== undefined && (
              <span className="badge text-bg-light ms-1">{scoreCounts.b30_50}</span>
            )}
          </button>
          <button
            type="button"
            className={`btn btn-sm ${f.needsReviewOnly ? 'btn-secondary' : 'btn-outline-secondary'}`}
            onClick={() => set({ needsReviewOnly: !f.needsReviewOnly })}
          >
            Needs review only
            {fx.needs_review !== undefined && (
              <span className="badge text-bg-light ms-1">{fx.needs_review}</span>
            )}
          </button>
        </div>

        {/* Search by AHRI ID / model number */}
        <div className="row g-2 mb-2">
          <div className="col-12 col-md-3">
            <label className="form-label small mb-1">AHRI ID</label>
            <input className="form-control form-control-sm" value={f.ahriId || ''}
              onChange={(e) => set({ ahriId: e.target.value })} placeholder="Search AHRI ID" />
          </div>
          <div className="col-12 col-md-3">
            <label className="form-label small mb-1">Indoor model number</label>
            <input className="form-control form-control-sm" value={f.indoorModelNumber || ''}
              onChange={(e) => set({ indoorModelNumber: e.target.value })} placeholder="Search indoor model #" />
          </div>
          <div className="col-12 col-md-3">
            <label className="form-label small mb-1">Outdoor model number</label>
            <input className="form-control form-control-sm" value={f.outdoorModelNumber || ''}
              onChange={(e) => set({ outdoorModelNumber: e.target.value })} placeholder="Search outdoor model #" />
          </div>
          <div className="col-12 col-md-3">
            <label className="form-label small mb-1">Furnace model number</label>
            <input className="form-control form-control-sm" value={f.furnaceModelNumber || ''}
              onChange={(e) => set({ furnaceModelNumber: e.target.value })} placeholder="Search furnace model #" />
          </div>
        </div>

        {/* Facet multi-selects (only rendered when values exist) */}
        <div className="row g-2 mb-2">
          <div className="col-12 col-md-4 col-lg-3">
            <FacetMultiSelect label="Field" options={fx.fields} selected={f.fields || []} onChange={(v) => set({ fields: v })} />
          </div>
          <div className="col-12 col-md-4 col-lg-3">
            <FacetMultiSelect label="AHRI brand" options={fx.ahri_brands} selected={f.ahriBrands || []} onChange={(v) => set({ ahriBrands: v })} />
          </div>
          <div className="col-12 col-md-4 col-lg-3">
            <FacetMultiSelect label="Candidate manufacturer" options={fx.candidate_manufacturers} selected={f.candidateManufacturers || []} onChange={(v) => set({ candidateManufacturers: v })} />
          </div>
          <div className="col-12 col-md-4 col-lg-3">
            <FacetMultiSelect label="Review reason" options={fx.review_reasons} selected={f.reviewReasons || []} onChange={(v) => set({ reviewReasons: v })} />
          </div>
          <div className="col-12 col-md-4 col-lg-3">
            <FacetMultiSelect label="Program" options={fx.programs} selected={f.programs || []} onChange={(v) => set({ programs: v })} />
          </div>
          <div className="col-12 col-md-4 col-lg-3">
            <FacetMultiSelect label="AHRI type" options={fx.ahri_types} selected={f.ahriTypes || []} onChange={(v) => set({ ahriTypes: v })} />
          </div>
        </div>

        {/* Preset bands */}
        <div className="row g-3 mb-2">
          <div className="col-12 col-lg-4">
            <Segmented label="Score band" presets={SCORE_PRESETS} value={f.scoreBucket || ''} onPick={(k) => set({ scoreBucket: k })} counts={scoreCounts} />
          </div>
          <div className="col-12 col-lg-4">
            <Segmented label="Manufacturer alignment" presets={ALIGNMENT_PRESETS} value={f.alignmentBucket || ''} onPick={(k) => set({ alignmentBucket: k })} counts={alignCounts} />
          </div>
          <div className="col-12 col-lg-4">
            <Segmented label="Part-match strength" presets={PART_STRENGTH_PRESETS} value={f.partStrengthPreset || ''} onPick={(k) => set({ partStrengthPreset: k })} />
          </div>
        </div>

        {/* Advanced regex (collapsed by default) */}
        <button type="button" className="btn btn-link btn-sm p-0 mb-2" onClick={() => setShowAdvanced((v) => !v)}>
          {showAdvanced ? '▾ Hide advanced (regex)' : '▸ Advanced (regex)'}
        </button>
        {showAdvanced && (
          <div className="row g-2 mb-2">
            <div className="col-12 col-md-6">
              <label className="form-label small mb-1">Part number matches (regex)</label>
              <input className="form-control form-control-sm font-monospace" value={f.partNumberRegex || ''}
                onChange={(e) => set({ partNumberRegex: e.target.value })} placeholder="^SM3.*TXV$" />
            </div>
            <div className="col-12 col-md-6">
              <label className="form-label small mb-1">AHRI source value (regex)</label>
              <input className="form-control form-control-sm font-monospace" value={f.sourceValueRegex || ''}
                onChange={(e) => set({ sourceValueRegex: e.target.value })} placeholder="optional" />
            </div>
          </div>
        )}

        <div className="d-flex justify-content-end gap-2">
          <button type="button" className="btn btn-outline-secondary btn-sm" onClick={onReset} disabled={loading}>
            Reset
          </button>
          <button type="button" className="btn btn-primary btn-sm" onClick={onApply} disabled={loading}>
            {loading ? 'Loading…' : 'Apply filter'}
          </button>
        </div>
      </div>
    </div>
  );
}
